import { usePostHog as usePH } from 'posthog-js/react';

import { PostHogEvent } from '@configs';

/**
 * useEvaPostHog is a safe wrapper around PostHog's React hook.
 *
 * This hook ensures PostHog methods (capture, identify, reset) are:
 * - No-ops when analytics are disabled (e.g. POSTHOG_ENABLED is not true)
 * - Guarded against missing PostHogProvider
 * - Debug-friendly: logs skipped events for visibility during development
 *
 * To enable PostHog tracking, set the `POSTHOG_ENABLED` environment variable to `"true"`.
 * In environments where this flag is not set, analytics methods will be disabled and logged.
 *
 * This wrapper provides a single integration point for PostHog usage across the app.
 */
export const useEvaPostHog = () => {
  const posthog = usePH();
  const isEnabled = POSTHOG_ENABLED;

  /**
   * Safely captures an event with optional properties.
   * Logs a debug message if PostHog is disabled or unavailable.
   * @param event - Must be a valid PostHogEvent from @configs/posthog-events
   */
  const capture = (event: PostHogEvent, props?: Record<string, unknown>) => {
    if (!isEnabled || !posthog) {
      // eslint-disable-next-line no-console
      console.debug(`[PostHog] Skipped event: '${event}'`, props);
      return;
    }

    posthog.capture?.(event, props);
  };

  /**
   * Associates an identity with the current user session.
   * Useful after login or fetching user data.
   */
  const identify = (userId: string, props?: Record<string, unknown>) => {
    if (!isEnabled || !posthog) {
      // eslint-disable-next-line no-console
      console.debug(`[PostHog] Skipped identify for user: '${userId}'`, props);
      return;
    }

    posthog.identify?.(userId, props);
  };

  /**
   * Resets the current PostHog session.
   * Typically used during logout.
   */
  const reset = () => {
    if (!isEnabled || !posthog) {
      // eslint-disable-next-line no-console
      console.debug(`[PostHog] Skipped reset (PostHog not initialized)`);
      return;
    }

    posthog.reset?.();
  };

  /**
   * Checks if a feature flag is enabled.
   * Returns true if PostHog is disabled (development mode).
   * Only enforces feature flags when PostHog is enabled (production).
   */
  const isFeatureEnabled = (flag: string): boolean => {
    if (!isEnabled || !posthog) {
      // eslint-disable-next-line no-console
      console.debug(`[PostHog] Feature flag '${flag}' allowed (PostHog not enabled - development mode)`);
      return true;
    }

    const flagEnabled = posthog.isFeatureEnabled?.(flag) ?? false;
    // eslint-disable-next-line no-console
    console.debug(`[PostHog] Feature flag '${flag}' ${flagEnabled ? 'enabled' : 'disabled'}`);
    return flagEnabled;
  };

  return { capture, identify, reset, isFeatureEnabled };
};
